import { emitGovernorLiveAction } from '@/lib/governorEventBus';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  EntryPrivacy,
  DatePrecision,
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import { toast } from '@/components/ui/sonner';
import {
  Loader2,
  Plus,
  X,
  Upload,
  ImageIcon,
  Film,
  CheckCircle2,
  AlertCircle,
  Lock,
  Users as UsersIcon,
  ShieldCheck,
  Calendar as CalendarIcon,
  UserCircle2,
  Tag,
} from 'lucide-react';

type Kind = 'image' | 'video';

interface Props {
  open: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  ownerUserId: string | null;
  kind: Kind;
  /** Set member's avatar to the first uploaded image if it doesn't have one yet. */
  setAvatarIfMissing?: boolean;
  hasAvatar?: boolean;
  /**
   * Optional pre-selected subject member ids for the "Who is this memory
   * about?" picker. When omitted, the modal defaults to selecting the
   * anchor `memberId` (i.e. uploading from a profile auto-tags THAT
   * person as a subject). Pass `[viewerMemberId]` from a dashboard /
   * "Me" upload entry-point to default-select the viewer.
   */
  defaultSubjectMemberIds?: string[];
  /** Remaining upload allowance. Null means unlimited. */
  maxUploadItems?: number | null;
  /** Final ownership gate supplied by Profile Ownership Authority. */
  authorityGranted?: boolean;
}

interface QueueItem {
  id: string;
  file: File;
  previewUrl: string;
  caption: string;
  // Date assigned to this memory. Defaults to today on add. User can pick
  // any past date or clear it to "unknown" via the precision selector.
  eventDate: string; // YYYY-MM-DD; '' when datePrecision==='unknown'
  datePrecision: DatePrecision;
  /**
   * Upload pipeline status.
   *  - 'queued'    → waiting in the queue, nothing has happened yet.
   *  - 'uploading' → file bytes are being streamed to Supabase Storage.
   *                  Progress climbs 8% → 90% during this phase.
   *  - 'saving'    → storage finished; we are now inserting the
   *                  family_graph_photos row. Progress is pinned at 95%
   *                  and the UI shows "Saving memory…" so the user
   *                  understands the second phase is happening — videos
   *                  used to feel "stuck at 90%" because the file
   *                  upload completed but the DB save was silent.
   *  - 'done'      → DB row landed; visible in the timeline after refresh.
   *  - 'error'     → either storage failed OR the DB insert failed. The
   *                  errorMsg makes the difference explicit so users
   *                  know whether to retry the upload or report a
   *                  database problem.
   */
  status: 'queued' | 'uploading' | 'saving' | 'done' | 'error';
  progress: number;
  errorMsg?: string;
  /**
   * True when storage upload succeeded but the family_graph_photos
   * INSERT failed. Surfaces a distinct error message ("uploaded the
   * file but couldn't save the memory record") so the user knows the
   * file did reach the server — useful diagnostic info.
   */
  dbInsertFailed?: boolean;
}


const todayISO = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};


const MAX_IMG_BYTES = 15 * 1024 * 1024; // 15 MB per photo
const MAX_VID_BYTES = 200 * 1024 * 1024; // 200 MB per video

// RFC-4122 UUID matcher. We use this as a hard pre-flight guard before any
// upload — the family_graph_photos.member_id column is a Postgres UUID, so
// passing the synthesized "self" string from the tree's "You" card produces
// `invalid input syntax for type uuid: "self"`. The "You" node should be
// resolved to the user's real self-anchor row upstream (FamilyGraphSection),
// but this guard makes the failure mode explicit if any caller ever slips.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Two simple privacy options. The DB still understands a third 'shared' value
// for any rows created before this simplification, but we no longer expose it
// in the UI — every new upload is either visible to the whole KinsCircle, or
// to the uploader only. Nothing is ever public.
type SimplePrivacy = Extract<EntryPrivacy, 'family' | 'private'>;

const PRIVACY_OPTIONS: Array<{
  value: SimplePrivacy;
  label: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: string; // tailwind classes for the active card border + bg
}> = [
  {
    value: 'family',
    label: 'Family Only',
    desc: 'Visible to everyone in your KinsCircle',
    icon: UsersIcon,
    tone: 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800',
  },
  {
    value: 'private',
    label: 'Only Me',
    desc: 'Just for you. No one else can see this.',
    icon: Lock,
    tone: 'border-slate-500 bg-slate-50 text-slate-800',
  },
];


/**
 * Multi-file photo / video upload modal for KinsCircle profiles.
 *
 * Privacy (simplified, two options only):
 *  - Default = 'family' — visible to claimed members of this KinsCircle.
 *  - 'private' = only the uploader (and the graph owner via RLS).
 *  - Nothing is ever public. RLS on family_graph_photos enforces this even
 *    if someone guesses a media URL.
 *
 * NEW — Memory Subject Assignment:
 *  - Every upload can optionally be tagged with one or more "subject"
 *    family members ("Who is this memory about?"). When subjects are
 *    selected, the SAME file (no duplication) appears on each subject's
 *    Journey timeline. When no subjects are selected, the upload is a
 *    general "Family memory" tied only to the anchor profile + family
 *    feed. Subjects are persisted in the existing `tagged_member_ids`
 *    column via the `subjectMemberIds` PhotoMeta alias — zero schema
 *    change, edits remain possible later via `updatePhoto`.
 */
const MediaUploadModal: React.FC<Props> = ({
  open,
  onClose,
  memberId,
  memberName,
  ownerUserId,
  kind,
  setAvatarIfMissing = false,
  hasAvatar = false,
  defaultSubjectMemberIds,
  maxUploadItems = null,
  authorityGranted = true,
}) => {
  const { addPhoto, updateMember, members, viewerMemberId } = useFamilyGraph();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [didUpload, setDidUpload] = useState(false);
  const [privacy, setPrivacy] = useState<SimplePrivacy>('family');

  /**
   * "Who is this memory about?" — set of family-graph member ids the
   * current upload batch is assigned to. Persisted to `tagged_member_ids`
   * on save so each subject's Journey timeline shows the memory without
   * duplicating the underlying file. Empty set = general family memory.
   */
  const [subjectIds, setSubjectIds] = useState<Set<string>>(new Set());

  /**
   * Live family roster shown as selectable chips. We surface every
   * non-rejected member so users can tag anyone in the tree (including
   * deceased relatives — the timeline supports posthumous memories via
   * the `legacy` life-stage). Sorted with the anchor profile first, then
   * the viewer's own claimed profile, then everyone else by name. The
   * synthetic 'self' tree placeholder is excluded because it has no DB
   * row (it's the implicit "You" anchor, not a real member).
   */
  const subjectCandidates = useMemo(() => {
    const list = members.filter(
      (m) => m.id !== 'self' && m.approvalStatus !== 'rejected',
    );
    return list.sort((a, b) => {
      // Pin the anchor profile (who the modal opened on) to the top.
      if (a.id === memberId && b.id !== memberId) return -1;
      if (b.id === memberId && a.id !== memberId) return 1;
      // Then the viewer's own claimed profile.
      if (a.id === viewerMemberId && b.id !== viewerMemberId) return -1;
      if (b.id === viewerMemberId && a.id !== viewerMemberId) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [members, memberId, viewerMemberId]);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      setItems([]);
      setBusy(false);
      setDidUpload(false);
      setPrivacy('family');
      // Default subject assignment:
      //  - If the caller passed an explicit list (e.g. dashboard "Me"
      //    upload → [viewerMemberId]), honor it.
      //  - Otherwise default-select the anchor member (uploading from
      //    a profile assumes the photo is at least about THAT person).
      //  - The synthetic 'self' string is never written to subjects —
      //    fall back to the viewer's real claimed-row id when the
      //    anchor is the "You" placeholder.
      const initialIds: string[] = (() => {
        if (defaultSubjectMemberIds && defaultSubjectMemberIds.length > 0) {
          return defaultSubjectMemberIds.filter(
            (id) => id && id !== 'self' && UUID_RE.test(id),
          );
        }
        if (memberId === 'self') {
          return viewerMemberId && UUID_RE.test(viewerMemberId)
            ? [viewerMemberId]
            : [];
        }
        return UUID_RE.test(memberId) ? [memberId] : [];
      })();
      setSubjectIds(new Set(initialIds));
      // Open the picker right away — feels native, like a phone share sheet.
      window.setTimeout(() => inputRef.current?.click(), 80);
    } else {
      // Cleanup blob URLs when modal closes.
      setItems((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return [];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const acceptAttr = kind === 'image' ? 'image/*' : 'video/*';
  const maxBytes = kind === 'image' ? MAX_IMG_BYTES : MAX_VID_BYTES;
  const labelKind = kind === 'image' ? 'photo' : 'video';
  const labelKindPlural = kind === 'image' ? 'photos' : 'videos';

  const addFiles = (fileList: FileList | File[]) => {
    if (!authorityGranted) {
      toast.error(
        'You do not have authority to add media to this profile.',
      );
      return;
    }

    const incoming = Array.from(fileList);

    const remaining =
      maxUploadItems === null
        ? incoming.length
        : Math.max(
            0,
            maxUploadItems - items.length,
          );

    if (remaining === 0) {
      toast.error(
        'Family Contributor photo limit reached. Upgrade to add more memories.',
      );
      return;
    }

    if (
      maxUploadItems !== null &&
      incoming.length > remaining
    ) {
      toast.error(
        `You can add ${remaining} more photo${
          remaining === 1 ? '' : 's'
        }.`,
      );
    }

    const allowedIncoming =
      maxUploadItems === null
        ? incoming
        : incoming.slice(0, remaining);

    const next: QueueItem[] = [];
    for (const f of allowedIncoming) {
      if (kind === 'image' && !f.type.startsWith('image/')) {
        toast.error(`${f.name}: not an image file`);
        continue;
      }
      if (kind === 'video' && !f.type.startsWith('video/')) {
        toast.error(`${f.name}: not a video file`);
        continue;
      }
      if (f.size > maxBytes) {
        toast.error(`${f.name} is ${formatBytes(f.size)} — max ${formatBytes(maxBytes)}`);
        continue;
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        caption: '',
        // REQUIRED date metadata — defaults to today, user can change it
        // to any past date below. This is the new "every memory has a date"
        // contract: photos & videos slot into the timeline immediately.
        eventDate: todayISO(),
        datePrecision: 'exact',
        status: 'queued',
        progress: 0,
      });
    }
    if (next.length > 0) {
      setItems((prev) => [...prev, ...next]);
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const tgt = prev.find((p) => p.id === id);
      if (tgt) URL.revokeObjectURL(tgt.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const updateCaption = (id: string, caption: string) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, caption } : p)));
  };

  const updateItemDate = (id: string, eventDate: string) => {
    setItems((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        // If the user clears the date, fall back to "unknown" precision so
        // the row still saves cleanly. Otherwise keep their chosen precision
        // (default exact) but never lower it accidentally.
        if (!eventDate) {
          return { ...p, eventDate: '', datePrecision: 'unknown' };
        }
        return {
          ...p,
          eventDate,
          datePrecision: p.datePrecision === 'unknown' ? 'exact' : p.datePrecision,
        };
      })
    );
  };

  const updateItemPrecision = (id: string, datePrecision: DatePrecision) => {
    setItems((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        if (datePrecision === 'unknown') {
          return { ...p, datePrecision, eventDate: '' };
        }
        // Switching back to a real precision after "unknown" → restore today.
        const eventDate = p.eventDate || todayISO();
        return { ...p, datePrecision, eventDate };
      })
    );
  };

  const toggleSubject = (id: string) => {
    setSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };




  const uploadOne = async (item: QueueItem, isFirstSuccessful: boolean): Promise<boolean> => {
    if (!authorityGranted) {
      throw new Error(
        'Profile media ownership authority denied this upload.',
      );
    }

    if (!memberId || !UUID_RE.test(memberId)) {
      setItems((prev) =>
        prev.map((p) =>
          p.id === item.id
            ? {
                ...p,
                status: 'error',
                progress: 0,
                errorMsg:
                  'Could not link upload to a profile. Please reopen this profile and try again.',
              }
            : p
        )
      );
      return false;
    }
    setItems((prev) =>
      prev.map((p) => (p.id === item.id ? { ...p, status: 'uploading', progress: 8 } : p))
    );
    const tick = window.setInterval(() => {
      setItems((prev) =>
        prev.map((p) =>
          p.id === item.id && p.status === 'uploading'
            ? { ...p, progress: Math.min(90, p.progress + Math.max(2, (90 - p.progress) * 0.08)) }
            : p
        )
      );
    }, 150);

    // Track which phase we're in so the catch block can show a phase-
    // specific error. 'storage' = file bytes upload to Supabase Storage.
    // 'saving'  = INSERT into family_graph_photos. Videos historically
    // appeared "stuck at 90%" because storage finished but the DB insert
    // was silent — surfacing this distinction is the whole point of the
    // explicit 'saving' status.
    let phase: 'storage' | 'saving' = 'storage';
    let storagePathToCleanup: string | null = null;

    try {
      const file = item.file;
      const ext = (file.name.split('.').pop() || (kind === 'image' ? 'jpg' : 'mp4'))
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      const safeUser = ownerUserId ? ownerUserId.replace(/[^a-zA-Z0-9-]/g, '') : 'anon';
      const folder = kind === 'image' ? 'graph-photos' : 'graph-videos';
      const path = `${folder}/${safeUser}/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('family-media')
        .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
      if (upErr) throw upErr;

      // Storage phase complete. Stop the synthetic 0→90% tick and flip
      // the visible status to 'saving' with progress=95 so the user
      // sees an explicit "Saving memory…" indicator. This is the fix
      // for the "video stalls around 90%" complaint — the bytes are
      // already on the server; we're now writing the database row.
      window.clearInterval(tick);
      phase = 'saving';
      storagePathToCleanup = path;
      setItems((prev) =>
        prev.map((p) =>
          p.id === item.id ? { ...p, status: 'saving', progress: 95 } : p
        )
      );

      const { data } = supabase.storage.from('family-media').getPublicUrl(path);

      // If this is the very first photo for a member with no avatar yet,
      // promote it to the avatar — but ONLY if the upload is family-visible.
      // We don't want a "Only me" upload to leak as the public avatar.
      if (
        kind === 'image' &&
        setAvatarIfMissing &&
        !hasAvatar &&
        isFirstSuccessful &&
        privacy === 'family'
      ) {
        try {
          await updateMember(memberId, { photoUrl: data.publicUrl });
        } catch {
          /* non-fatal */
        }
      }

      // Filter the subject set to valid UUIDs only — the synthetic 'self'
      // placeholder must never reach the DB, and any stale state is
      // dropped silently. Empty array = general "Family memory".
      const cleanSubjects = Array.from(subjectIds).filter(
        (id) => id && id !== 'self' && UUID_RE.test(id),
      );

      await addPhoto(
        memberId,
        data.publicUrl,
        path,
        item.caption.trim() || null,
        kind === 'image' ? 'image' : 'video',
        {
          privacy,
          // 'shared' is no longer a UI option, so this batch never targets
          // selected members. Always pass an empty array.
          sharedWithMemberIds: [],
          // Date metadata — every photo / video lands on the family
          // timeline at the moment it actually happened.
          eventDate:
            item.datePrecision === 'unknown' || !item.eventDate
              ? null
              : item.eventDate,
          datePrecision: item.datePrecision,
          // NEW — subject assignment ("Who is this memory about?").
          // Persisted in the existing `tagged_member_ids` column so each
          // subject's Journey timeline shows this memory. The file is
          // stored ONCE — there is no duplication.
          subjectMemberIds: cleanSubjects,
        }
      );

      setItems((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: 'done', progress: 100 } : p))
      );
      return true;
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MediaUploadModal] upload failed', { phase, err });

      // Phase-specific user-facing message. The DB-insert failure is the
      // important one — it means the file IS already in storage but the
      // memory record didn't save. Without this distinction users see
      // "Upload failed" and assume they need to retry the entire upload,
      // when really the file is already on the server.
      const rawMsg = (err?.message || '').toString();
      let friendly: string;
      if (phase === 'saving') {
        // Detect the specific PostgREST schema-cache error so we can
        // surface a precise diagnostic instead of a generic message.
        const looksLikeSchemaCache =
          /schema cache|column .* does not exist|could not find the .* column/i.test(rawMsg);
        if (looksLikeSchemaCache) {
          friendly =
            'Uploaded the file, but the memory record could not be saved (database schema is out of date). Please refresh the page and try again.';
        } else {
          friendly =
            rawMsg
              ? `Uploaded the file, but could not save the memory record: ${rawMsg}`
              : 'Uploaded the file, but could not save the memory record. Please try again.';
        }

        // Best-effort cleanup of the orphaned storage object so we
        // don't leave dangling files when the DB insert failed.
        //
        // ── LEGACY DATA POLICY (LEGACY_DATA_POLICY.md §6) ──
        // This is the ONLY storage removal in the upload pipeline, and
        // it runs ONLY in the `phase === 'saving'` catch branch — i.e.
        // when the family_graph_photos INSERT failed and therefore NO
        // saved memory record references this storage object. Removing
        // it cannot lose any user-visible memory. This cleanup MUST
        // NEVER be expanded to remove storage objects based on the
        // owner's billing state, the upload's age, or any signal other
        // than "the matching DB row failed to land."
        if (storagePathToCleanup) {
          supabase.storage
            .from('family-media')
            .remove([storagePathToCleanup])
            .catch(() => {
              /* non-fatal — orphan cleanup is best-effort */
            });
        }

      } else {
        friendly = rawMsg || 'Upload failed. Please try again.';
      }

      setItems((prev) =>
        prev.map((p) =>
          p.id === item.id
            ? {
                ...p,
                status: 'error',
                progress: 0,
                errorMsg: friendly,
                dbInsertFailed: phase === 'saving',
              }
            : p
        )
      );
      // Surface a distinct toast for DB-insert failures so the user
      // knows the file did reach the server but the record didn't save.
      if (phase === 'saving') {
        toast.error('Could not save memory record', {
          description: friendly,
        });
      }
      return false;
    } finally {
      window.clearInterval(tick);
    }
  };


  const handleDone = async () => {
    const queued = items.filter((p) => p.status === 'queued' || p.status === 'error');
    if (queued.length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    let successCount = 0;
    for (let i = 0; i < queued.length; i++) {
      const ok = emitGovernorLiveAction('media_upload_attempt', { source: 'MediaUploadModal' });
      await uploadOne(queued[i], successCount === 0);
      if (ok) successCount += 1;
    }
    setBusy(false);
    setDidUpload(true);
    if (successCount > 0) {
      const subjectCount = subjectIds.size;
      toast.success(
        `${successCount} ${successCount === 1 ? labelKind : labelKindPlural} added to ${memberName.split(' ')[0]}`
      );
      // ── Smart contextual suggestion (non-intrusive). Honors the
      // "if memory has people assigned, suggest inviting them; if it
      // has none, suggest tagging who it's about" rule. Pure UI hint —
      // never blocks closing the modal.
      window.setTimeout(() => {
        if (subjectCount === 0) {
          toast.message('Tip: tag who this memory is about', {
            description:
              'Add it to their timeline by editing the photo and selecting a family member.',
          });
        } else if (subjectCount > 0) {
          // If any subject is still an unclaimed placeholder, gently
          // nudge the user to invite them so they can see the memory
          // tied to their own profile.
          const unclaimed = Array.from(subjectIds)
            .map((id) => members.find((m) => m.id === id))
            .filter(
              (m) =>
                m &&
                m.id !== viewerMemberId &&
                m.profileState !== 'claimed' &&
                !m.claimedByUserId,
            );
          if (unclaimed.length > 0) {
            const first = unclaimed[0]!;
            toast.message(
              `Invite ${first.name.split(' ')[0]} to KinsCircle?`,
              {
                description:
                  'They can see memories connected to them once they join.',
              },
            );
          }
        }
      }, 800);
      window.setTimeout(() => {
        onClose();
      }, 700);
    } else {
      toast.error(`Could not upload — see errors above.`);
    }
  };


  if (!open) return null;

  const queuedCount = items.filter((p) => p.status === 'queued' || p.status === 'error').length;
  const successCount = items.filter((p) => p.status === 'done').length;
  const subjectCount = subjectIds.size;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white px-5 py-4 flex items-center gap-3">
          {kind === 'image' ? (
            <ImageIcon className="w-5 h-5" />
          ) : (
            <Film className="w-5 h-5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-serif font-bold text-lg leading-tight">
              {kind === 'image' ? 'Add photos' : 'Add videos'}
            </div>
            <div className="text-[11px] opacity-90 truncate">
              For {memberName} · {items.length === 0 ? 'choose files to begin' : `${items.length} selected`}
            </div>
          </div>
          <button
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="text-white/85 hover:text-white p-1 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Privacy chooser — applies to ALL files in this batch. */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-fuchsia-600" />
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                Who can see {items.length > 1 ? 'these' : 'this'}?
              </div>
            </div>
            <div className="p-2 grid grid-cols-2 gap-2">
              {PRIVACY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = privacy === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={busy}
                    onClick={() => setPrivacy(opt.value)}
                    className={`text-left rounded-lg border-2 px-3 py-2.5 transition disabled:opacity-50 ${
                      active
                        ? opt.tone
                        : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" />
                      <span className="font-bold text-[12px]">{opt.label}</span>
                      {opt.value === 'family' && (
                        <span className="ml-auto text-[9px] uppercase tracking-wider opacity-70">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] mt-0.5 leading-tight">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* NEW — Subject assignment ("Who is this memory about?").
              Multi-select chips of family members. Empty selection saves
              the upload as a general Family Memory; otherwise the same
              file appears on each subject's Journey timeline. The anchor
              profile is pre-selected so the typical "uploading from a
              profile" flow tags that person automatically. */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
              <Tag className="w-4 h-4 text-fuchsia-600" />
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex-1 min-w-0">
                Who {items.length > 1 ? 'are these memories' : 'is this memory'} about?
              </div>
              <span className="text-[10px] text-slate-500">
                {subjectCount === 0
                  ? 'Family memory'
                  : `${subjectCount} selected`}
              </span>
            </div>
            <div className="p-2">
              {subjectCandidates.length === 0 ? (
                <div className="text-[11px] text-slate-500 italic px-2 py-1.5">
                  Add family members to your tree to tag memories about them.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {subjectCandidates.map((m) => {
                    const active = subjectIds.has(m.id);
                    const isAnchor = m.id === memberId;
                    const isMe = m.id === viewerMemberId;
                    const label = isMe
                      ? `Me${m.name ? ` · ${m.name.split(' ')[0]}` : ''}`
                      : m.name || 'Unnamed';
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={busy}
                        onClick={() => toggleSubject(m.id)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${
                          active
                            ? 'bg-fuchsia-600 border-fuchsia-600 text-white shadow-sm'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-fuchsia-50 hover:border-fuchsia-300'
                        }`}
                        title={
                          isAnchor
                            ? `${label} (this profile)`
                            : isMe
                            ? `${label} (you)`
                            : label
                        }
                      >
                        <UserCircle2 className="w-3 h-3" />
                        <span className="truncate max-w-[140px]">{label}</span>
                        {isAnchor && (
                          <span
                            className={`text-[8px] uppercase tracking-wider rounded px-1 ${
                              active
                                ? 'bg-white/20'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            Profile
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1.5 px-1 leading-tight">
                {subjectCount === 0
                  ? 'No people selected → saves as a general Family Memory in the family feed.'
                  : 'Appears on each selected person\'s Journey timeline. The file is stored once.'}
              </div>
            </div>
          </div>

          {items.length === 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full rounded-2xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/60 hover:bg-fuchsia-50 px-4 py-10 flex flex-col items-center justify-center gap-2 text-center transition"
            >
              <Upload className="w-8 h-8 text-fuchsia-600" />
              <div className="font-bold text-slate-800">
                Choose {labelKindPlural} from your device
              </div>
              <div className="text-xs text-slate-500">
                Select multiple {labelKindPlural} at once · max{' '}
                {kind === 'image' ? '15 MB each' : '200 MB each'}
              </div>
            </button>
          ) : (

            <>
              {/* Preview list */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex flex-col"
                  >
                    <div className="relative aspect-square bg-black">
                      {kind === 'image' ? (
                        <img
                          src={it.previewUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={it.previewUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      )}
                      {it.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white gap-1">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-[10px] font-bold">{Math.round(it.progress)}%</span>
                          <span className="text-[9px] opacity-80">Uploading file…</span>
                        </div>
                      )}
                      {it.status === 'saving' && (
                        // Storage upload finished — DB row is being written.
                        // Distinct color + label so users see the second
                        // phase explicitly and don't think the upload is
                        // stuck near 90%.
                        <div className="absolute inset-0 bg-fuchsia-700/65 flex flex-col items-center justify-center text-white gap-1">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-[10px] font-bold">Saving memory…</span>
                          <span className="text-[9px] opacity-90">Writing to family timeline</span>
                        </div>
                      )}
                      {it.status === 'done' && (
                        <div className="absolute inset-0 bg-emerald-500/35 flex items-center justify-center text-white">
                          <CheckCircle2 className="w-7 h-7" />
                        </div>
                      )}
                      {it.status === 'error' && (
                        <div className="absolute inset-0 bg-rose-500/40 flex flex-col items-center justify-center text-white p-2 text-center">
                          <AlertCircle className="w-5 h-5" />
                          {it.dbInsertFailed && (
                            <span className="text-[8px] uppercase tracking-wider font-bold bg-rose-700/80 rounded px-1 mt-1">
                              File saved · record didn't
                            </span>
                          )}
                          <span className="text-[10px] font-semibold leading-tight mt-1">
                            {it.errorMsg || 'Failed'}
                          </span>
                        </div>
                      )}

                      {!busy && it.status !== 'done' && (
                        <button
                          type="button"
                          onClick={() => removeItem(it.id)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-500/95 hover:bg-rose-600 text-white flex items-center justify-center shadow"
                          aria-label="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={it.caption}
                      onChange={(e) => updateCaption(it.id, e.target.value)}
                      disabled={busy || it.status === 'done'}
                      placeholder="Caption (optional)"
                      className="text-xs px-2 py-1.5 border-t border-slate-200 outline-none focus:bg-fuchsia-50/50 disabled:bg-slate-100"
                    />
                    {/* When did this happen? — REQUIRED, defaults to today.
                        Users can pick any past date so a scanned childhood
                        photo or a 1995 wedding video lands at the right
                        place on the family timeline. Setting "Unknown date"
                        keeps the file uploadable but groups it under the
                        Undated bucket. */}
                    <div className="border-t border-slate-200 bg-white px-2 py-1.5 flex items-center gap-1.5">
                      <CalendarIcon className="w-3 h-3 text-fuchsia-600 shrink-0" />
                      <input
                        type="date"
                        value={it.eventDate}
                        max={todayISO()}
                        onChange={(e) => updateItemDate(it.id, e.target.value)}
                        disabled={busy || it.status === 'done' || it.datePrecision === 'unknown'}
                        className="flex-1 min-w-0 text-[11px] outline-none bg-transparent disabled:text-slate-400"
                        aria-label="Date this happened"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          updateItemPrecision(
                            it.id,
                            it.datePrecision === 'unknown' ? 'exact' : 'unknown'
                          )
                        }
                        disabled={busy || it.status === 'done'}
                        className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 transition ${
                          it.datePrecision === 'unknown'
                            ? 'bg-slate-200 text-slate-700'
                            : 'bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100'
                        } disabled:opacity-40`}
                        title="Toggle unknown date"
                      >
                        {it.datePrecision === 'unknown' ? 'Set date' : 'Unknown'}
                      </button>
                    </div>
                    <div className="px-2 py-1 text-[9px] text-slate-500 flex items-center justify-between border-t border-slate-100">
                      <span className="truncate">{it.file.name}</span>
                      <span className="ml-1 shrink-0">{formatBytes(it.file.size)}</span>
                    </div>

                  </div>
                ))}

                {!busy && (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="aspect-square rounded-xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/60 hover:bg-fuchsia-50 flex flex-col items-center justify-center gap-1 text-fuchsia-700 transition"
                  >
                    <Plus className="w-6 h-6" />
                    <span className="text-[11px] font-bold">Add more</span>
                  </button>
                )}
              </div>

              {busy && (() => {
                // When ANY tile is in the 'saving' phase, surface that
                // explicitly in the global banner. Otherwise the older
                // "Uploading…" copy stays. This is the user-facing
                // signal that the file already reached the server and
                // we are now writing the family_graph_photos record.
                const anySaving = items.some((p) => p.status === 'saving');
                return (
                  <div className={`rounded-xl border p-3 flex items-center gap-2 text-sm ${
                    anySaving
                      ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-900'
                      : 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800'
                  }`}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {anySaving
                      ? 'Saving memory to your family timeline… please don\'t close this window.'
                      : 'Uploading… please don\'t close this window.'}
                  </div>
                );
              })()}

              {didUpload && successCount > 0 && !busy && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  {successCount} {successCount === 1 ? labelKind : labelKindPlural} added.
                </div>
              )}
            </>
          )}

          <input
            ref={inputRef}
            type="file"
            accept={acceptAttr}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500 flex items-center gap-1.5 min-w-0">
            {privacy === 'family' ? (
              <UsersIcon className="w-3.5 h-3.5 text-fuchsia-600 shrink-0" />
            ) : (
              <Lock className="w-3.5 h-3.5 text-slate-700 shrink-0" />
            )}
            <span className="truncate">
              {privacy === 'family'
                ? 'Visible to your KinsCircle'
                : 'Only you can see this'}
            </span>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={busy ? undefined : onClose}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {didUpload && queuedCount === 0 ? 'Close' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleDone}
              disabled={busy || items.length === 0 || queuedCount === 0}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:opacity-95 text-white disabled:opacity-40 inline-flex items-center gap-2"
            >

              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Done · Upload {queuedCount > 0 ? queuedCount : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaUploadModal;
